import React from "react";
import { Button } from "@progress/kendo-react-buttons";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Field, FieldArrayRenderProps } from "@progress/kendo-react-form";
import {
  CustomAppDialog,
  CustomDialogCloseButton,
} from "../../components/dialog/Dialog";
import { Typography } from "@progress/kendo-react-common";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { MdDelete } from "react-icons/md";
import IconButton from "../../components/common/IconButton";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import {
  closeBandhaiReceiptChargesDetailDeleteConfirmDialog,
  openBandhaiReceiptChargesDetailDeleteConfirmDialog,
  setBandhaiReceiptChargesDetailIndex,
} from "./bandhaiReceiptSlice";
import { IItem } from "../item/itemModel";
import FormDatePicker from "../../components/formFields/FormDateField";

const BandhaiGoldReceiveArray = (
  fieldArrayRenderProps: FieldArrayRenderProps,
) => {
  const dispatch = useAppDispatch();
  const BandhaiReceiptChargesDetailIndex = useAppSelector(
    (state) => state.bandhaireceipt.BandhaiReceiptChargesDetailIndex,
  );
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const isViewDialog = dialogName.startsWith("view-bandhai-gold-receive-");
  const BandhaiReceiptChargesDetailDeleteConfirmDialog = useAppSelector(
    (state) =>
      state.bandhaireceipt.BandhaiReceiptChargesDetailDeleteConfirmDialog,
  );
  const fieldName = fieldArrayRenderProps.name;
  const ItemList = useAppSelector((state) => state.item.ItemList);

  const handleDeleteAction = React.useCallback(
    async (index: any) => {
      if (fieldArrayRenderProps?.value[index]?.id) {
        fieldArrayRenderProps.onRemove({ index: index });
        dispatch(closeBandhaiReceiptChargesDetailDeleteConfirmDialog());
        dispatch(setBandhaiReceiptChargesDetailIndex(-1));
      } else {
        fieldArrayRenderProps.onRemove({ index: index });
        dispatch(closeBandhaiReceiptChargesDetailDeleteConfirmDialog());
        dispatch(setBandhaiReceiptChargesDetailIndex(-1));
      }
    },
    [fieldArrayRenderProps],
  );

  const pushElementInarray = React.useCallback(() => {
    fieldArrayRenderProps?.onPush({
      value: {
        GhatItemRowNum: fieldArrayRenderProps.value?.length + 1,
        ReceiveDate: new Date(),
        item_id: null,
        ReceiveWeight: null,
        pcs: null,
      },
    });
  }, [fieldArrayRenderProps]);

  const handleOpenDeleteDialog = (index: any) => {
    dispatch(openBandhaiReceiptChargesDetailDeleteConfirmDialog());
    dispatch(setBandhaiReceiptChargesDetailIndex(index));
  };

  return (
    <>
      <CustomAppDialog open={BandhaiReceiptChargesDetailDeleteConfirmDialog}>
        <>
          <Typography.h5>{"Delete Gold Receive Entry"}</Typography.h5>
          <GridLayout>
            <GridLayoutItem>
              <Typography.p>Are you sure you want to delete this?</Typography.p>
            </GridLayoutItem>
            <GridLayoutItem
              style={{
                display: "flex",
                justifyContent: "end",
              }}
            >
              <CustomDialogCloseButton
                themeColor="error"
                closeDialogFunction={
                  closeBandhaiReceiptChargesDetailDeleteConfirmDialog
                }
              />
              <Button
                fillMode={"solid"}
                themeColor={"error"}
                onClick={() =>
                  handleDeleteAction(BandhaiReceiptChargesDetailIndex)
                }
                type="button"
              >
                Delete
              </Button>
            </GridLayoutItem>
          </GridLayout>
        </>
      </CustomAppDialog>
      <GridLayout cols={[{ width: "50%" }, { width: "50%" }]}>
        <GridLayoutItem>
          <Typography.h5 style={{ marginBottom: 5 }}>
            Bandhai Gold Receive Details
          </Typography.h5>
        </GridLayoutItem>
        <GridLayoutItem style={{ textAlign: "left", marginLeft: "auto" }}>
          {!isViewDialog && (
            <Button
              fillMode={"solid"}
              themeColor={"primary"}
              onClick={pushElementInarray}
              type="button"
            >
              Add Item
            </Button>
          )}
        </GridLayoutItem>
      </GridLayout>
      {fieldArrayRenderProps.value &&
        fieldArrayRenderProps.value.length > 0 && (
          <div
            className="table-responsive"
            style={{
              maxHeight: isViewDialog ? "50vh" : "20vh",
              scrollbarWidth: "thin",
              marginTop: 10,
            }}
          >
            <table
              className="table table-striped table-bordered table-hover field-array-table"
              style={{ minWidth: "100%" }}
            >
              <thead>
                <tr>
                  <th style={{ width: "15%" }}>Receive Date</th>
                  <th style={{ width: "25%" }}>Item</th>
                  <th style={{ width: "15%" }}>Receive Weight (gm)</th>
                  <th style={{ width: "10%" }}>Pcs</th>
                  <th style={{ width: "3%", position: "sticky" }}></th>
                </tr>
              </thead>
              <tbody>
                {fieldArrayRenderProps.value &&
                  fieldArrayRenderProps.value.length > 0 &&
                  fieldArrayRenderProps.value.map(
                    (bandhai_gold_receive: any, index: number) => (
                      <tr key={index}>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.ReceiveDate`}
                            placeholder="Select Date"
                            component={FormDatePicker}
                            validator={requiredValidator}
                            disabled={isViewDialog}
                          />
                        </td>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.item_id`}
                            placeholder="Select Item"
                            component={FormSelectionField}
                            validator={requiredValidator}
                            options={ItemList?.map((item: IItem) => {
                              return {
                                value: item.id,
                                label: item.item_name,
                              };
                            })}
                            disabled={isViewDialog}
                          />
                        </td>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.ReceiveWeight`}
                            placeholder="0.000"
                            type="number"
                            format="n3"
                            component={FormNumericTextField}
                            validator={requiredValidator}
                            disabled={isViewDialog}
                          />
                        </td>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.pcs`}
                            placeholder="0"
                            type="number"
                            format="n0"
                            component={FormNumericTextField}
                            // validator={requiredValidator}
                            disabled={isViewDialog}
                          />
                        </td>
                        <td
                          style={{
                            position: "sticky",
                            right: 0,
                            zIndex: 2,
                          }}
                        >
                          <IconButton
                            type="button"
                            fillMode="solid"
                            size="small"
                            title="Delete"
                            themeColor="error"
                            style={{
                              position: "absolute",
                              height: 32,
                              width: 32,
                              right: 15,
                              pointerEvents: "all",
                            }}
                            onClick={() => handleOpenDeleteDialog(index)}
                          >
                            <MdDelete
                              className="absolute-position"
                              style={{ fontSize: "22px" }}
                            />
                          </IconButton>
                        </td>
                      </tr>
                    ),
                  )}
              </tbody>
            </table>
          </div>
        )}
    </>
  );
};

export default BandhaiGoldReceiveArray;
